import {
  Children,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import { isSlot } from '../../../core/utils/is-slot.js';
import { Divider } from '../../../layouts/divider/Divider.js';
import { ChartSeriesAction } from '../components/series-action/ChartSeriesAction.js';

const anyChildIsNotSeriesActionComponent = (children: ReactNode) => {
  return Children.toArray(children).some((child) => {
    return !Object.values(ChartSeriesAction).some(
      (ChartSeriesActionComponent) => isSlot(child, ChartSeriesActionComponent),
    );
  });
};

const sortResults = (
  seriesActions: (ReactElement | undefined)[],
  intents: (ReactElement | undefined)[],
) => {
  if (seriesActions.length === 0) {
    return intents;
  }
  if (intents.length === 0) {
    return seriesActions;
  }

  return [...seriesActions, <Divider key="divider" />, ...intents];
};

/**
 * Splits Intents and the rest of the menu items and adds a divider between them.
 *
 * Note: If some of the menu items are not of type ChartSeriesAction.*, it could mean that actions
 * are rendered via intermediate components. To avoid an expensive recursive loop for type checks,
 * we skip checking the component type entirely. In this case, the consumer needs to be in charge of
 * rendering a <Divider/> between actions and intents.
 *
 * @param children - element to render
 */
export function renderMenuActionChildren(
  children: ReactNode,
  options?: {
    divideActionsAndIntents?: boolean;
  },
) {
  if (!children) {
    return;
  }
  const { divideActionsAndIntents = true } = options || {};

  const seriesActions: (ReactElement | undefined)[] = [];
  const intents: (ReactElement | undefined)[] = [];

  if (anyChildIsNotSeriesActionComponent(children)) {
    return children;
  }

  Children.forEach(children, (child, index) => {
    if (isSlot(child, ChartSeriesAction.Intent)) {
      intents.push(child);
    } else if (isValidElement(child)) {
      seriesActions.push(child);
    } else {
      seriesActions.push(<Fragment key={index}>{child}</Fragment>);
    }
  });

  if (!divideActionsAndIntents) {
    return [...seriesActions, ...intents];
  }

  return intents.length > 0 || seriesActions.length > 0
    ? sortResults(seriesActions, intents)
    : undefined;
}
